import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./storyboard.scss";
import Loader from "../../../shared/loader";
import auth from "../../../shared/auth";
import InputSlider from "../../../shared/controls/input-slider";
import FilmanizeCheckbox from "../../../shared/filmanize-checkbox";
import WarningText from "../../../shared/warning-text";
import ShotMeta from "./shot-meta";
import Scrollspy from 'react-scrollspy';
import ReactDOM from 'react-dom';

const getShotImages = (shot) => {
    if (!shot) return [];
    if (shot.pictures) return shot.pictures.map((p) => p.url || p);
    if (shot.imageUrls) return shot.imageUrls;
    if (shot.images) return shot.images.map((i) => i.url);
    if (shot.mainImageUrl) return [shot.mainImageUrl];
    return [];
};

const Storyboard = ({ scenes = [], shotsByScene = {}, loadingShots, filmId, settings = null, saveSettings }: any) => {
    const [imagesPerRow, setImagesPerRow] = useState(2);
    const [includeDescription, setIncludeDescription] = useState(true);
    const [includeShotSize, setIncludeShotSize] = useState(false);
    const [includeLensFocalLength, setIncludeLensFocalLength] = useState(false);
    const [includeNotes, setIncludeNotes] = useState(false);
    const [includeCharacters, setIncludeCharacters] = useState(false);
    const [includeMovement, setIncludeMovement] = useState(false);

    const saveTimer = useRef<number | null>(null);
    const SAVE_DEBOUNCE = 500;

    // populate from saved settings when they arrive
    useEffect(() => {
        if (!settings) return;
        if (typeof settings.imagesPerRow === 'number') setImagesPerRow(settings.imagesPerRow);
        if (typeof settings.includeDescription === 'boolean') setIncludeDescription(settings.includeDescription);
        if (typeof settings.includeShotSize === 'boolean') setIncludeShotSize(settings.includeShotSize);
        if (typeof settings.includeLensFocalLength === 'boolean') setIncludeLensFocalLength(settings.includeLensFocalLength);
        if (typeof settings.includeNotes === 'boolean') setIncludeNotes(settings.includeNotes);
        if (typeof settings.includeCharacters === 'boolean') setIncludeCharacters(settings.includeCharacters);
        if (typeof settings.includeMovement === 'boolean') setIncludeMovement(settings.includeMovement);
    }, [settings]);

    useEffect(() => {
        return () => {
            if (saveTimer.current) window.clearTimeout(saveTimer.current as any);
        };
    }, []);

    const scheduleSave = (overrideSettings: any = null) => {
        if (!saveSettings) return;
        if (saveTimer.current) window.clearTimeout(saveTimer.current as any);
        saveTimer.current = window.setTimeout(() => {
            const payload = overrideSettings || {
                imagesPerRow,
                includeDescription,
                includeShotSize,
                includeLensFocalLength,
                includeNotes,
                includeCharacters,
                includeMovement
            };
            try {
                saveSettings(payload);
            } catch (e) {
                // swallow
            }
        }, SAVE_DEBOUNCE) as unknown as number;
    };

    const getCurrentSettings = () => ({
        imagesPerRow,
        includeDescription,
        includeShotSize,
        includeLensFocalLength,
        includeNotes,
        includeCharacters,
        includeMovement
    });

    const imageToken = auth.getImageToken();
    const [analyzed, setAnalyzed] = useState<any>({});

    const totalShots = (scenes || []).reduce((acc: number, s: any) => acc + ((s && s.shots) ? s.shots.length : 0), 0);
    const totalImages = (scenes || []).reduce((acc: number, s: any) => {
        if (!s || !s.shots) return acc;
        return acc + s.shots.reduce((a: number, sh: any) => a + getShotImages(sh).length, 0);
    }, 0);

    // scrollspy refs
    const nameElementRef = React.useRef<any>(null);
    const scrollRootRef = React.useRef<any>(null);
    const linkRefs = React.useRef<any>({});
    const sectionRefs = React.useRef<any>({});

    useEffect(() => {
        // ensure refs exist for scenes
        (scenes || []).forEach((s: any) => {
            if (!linkRefs.current[s.id]) linkRefs.current[s.id] = React.createRef();
            if (!sectionRefs.current[s.id]) sectionRefs.current[s.id] = React.createRef();
        });
    }, [scenes]);

    const sceneIds = (scenes || []).map(s => `${s.id}`);

    const scrollToSection = (id: string) => {
        const ref = sectionRefs.current[id];
        if (ref && ref.current) {
            const rect = ref.current.getBoundingClientRect();
            const top = rect.top + window.pageYOffset - 120;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    };

    const scrollSpyOnEvent = (e: any) => {
        if (!e) return;
        // scroll the left menu so current item is visible
        const r = linkRefs.current[e.id];
        if (nameElementRef.current && r && r.current) {
            try {
                nameElementRef.current.scrollTo({ top: r.current.offsetTop - 120, behavior: 'smooth' });
            } catch (err) {
                nameElementRef.current.scrollTop = r.current.offsetTop - 120;
            }
        }
    };

    const onImageLoad = (e: any, url: string) => {
        // don't re-analyze
        if (analyzed[url]) return;

        try {
            const imgEl = e.target as HTMLImageElement;
            const canvas = document.createElement("canvas");
            const w = 40;
            const h = 40;
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            if (!ctx) throw new Error("No canvas");
            ctx.drawImage(imgEl, 0, 0, w, h);
            const data = ctx.getImageData(0, 0, w, h).data;
            let total = 0;
            let count = 0;
            // sample pixels to reduce work
            for (let i = 0; i < data.length; i += 16) {
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];
                const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
                total += lum;
                count++;
            }
            const avg = total / Math.max(1, count);
            // higher avg => brighter image => use dark text
            const textColor = avg > 160 ? "dark" : "light";
            setAnalyzed((p: any) => ({ ...p, [url]: { textColor } }));
        } catch (err) {
            // likely CORS or canvas tainting -> fallback to boxed label
            setAnalyzed((p: any) => ({ ...p, [url]: { useBox: true } }));
        }
    };

    const onsetImagesPerRow = (e: any) => {
        const val = Number(e);
        if (!isNaN(val) && val >= 1 && val <= 6) {
            setImagesPerRow(val);
            scheduleSave({ ...getCurrentSettings(), imagesPerRow: val });
        }
    };

    const onToggle = (setter: (v: boolean) => void, key: string, value: boolean) => {
        setter(value);
        scheduleSave({ ...getCurrentSettings(), [key]: value });
    };

    // Decrease gap as the number of images per row increases
    const shotsGap = `${Math.max(4, 16 - imagesPerRow * 2)}px`;



    return (
        <div className="blade-content widest storyboard-page">
            <div className="storyboard-header">
                <h2 className="menu-label">Storyboard</h2>

                <div className="storyboard-controls">
                    <div style={{ minWidth: 260 }}>
                        <label className="label">Images per row</label>
                        <InputSlider
                            value={imagesPerRow}
                            onChange={onsetImagesPerRow}
                            minValue={1}
                            maxValue={6}
                        />
                    </div>
                    <span className="storyboard-controls-spacer" >
                        Show:
                    </span>
                    <FilmanizeCheckbox checked={includeDescription} onChange={(v) => onToggle(setIncludeDescription, 'includeDescription', v)} label="Description" />
                    <FilmanizeCheckbox checked={includeShotSize} onChange={(v) => onToggle(setIncludeShotSize, 'includeShotSize', v)} label="Shot size" />
                    <FilmanizeCheckbox checked={includeLensFocalLength} onChange={(v) => onToggle(setIncludeLensFocalLength, 'includeLensFocalLength', v)} label="Lens" />
                    <FilmanizeCheckbox checked={includeCharacters} onChange={(v) => onToggle(setIncludeCharacters, 'includeCharacters', v)} label="Characters" />
                    <FilmanizeCheckbox checked={includeMovement} onChange={(v) => onToggle(setIncludeMovement, 'includeMovement', v)} label="Movement" />
                    <FilmanizeCheckbox checked={includeNotes} onChange={(v) => onToggle(setIncludeNotes, 'includeNotes', v)} label="Notes" />
                </div>
            </div>
            {loadingShots && <Loader />}

            {!loadingShots && <>
                <div className="storyboard-inner">
                    <aside className="storyboard-menu">
                        <p className="menu-label">Scenes</p>
                        <ul className="menu-list" ref={elem => (nameElementRef.current = elem)} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: '8px', rowGap: 0 }}>
                            <Scrollspy items={sceneIds}
                                currentClassName='current'
                                componentTag='ol'
                                onUpdate={scrollSpyOnEvent}
                                style={{ display: 'contents' }}
                            >
                                {(scenes || []).map((scene: any) => {
                                    const sceneTitle = `${scene.number}. ${scene.settingName}`;
                                    const sceneName = `${scene.settingName}`;
                                    return (
                                        <li key={scene.id} style={{ display: 'contents' }}>
                                            <a className="" ref={linkRefs.current[scene.id]} onClick={() => scrollToSection(scene.id)} title={sceneTitle} style={{ gridColumn: '1 / 3', display: 'grid', gridTemplateColumns: 'subgrid', padding: '0.25rem 0.75rem' }}>
                                                <span style={{ gridColumn: '1' }}>{scene.number}</span>
                                                <span style={{ gridColumn: '2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sceneName}</span>
                                            </a>
                                        </li>
                                    )
                                })}

                            </Scrollspy>
                        </ul>
                    </aside>

                    <div className="storyboard-scroll-root" ref={(el) => (scrollRootRef.current = el)}>
                        <div className="storyboard-scenes">
                            {totalShots === 0 && (
                                <WarningText>No shots found. Add shots to scenes to build a storyboard.</WarningText>

                            )}

                            {totalShots > 0 && totalImages === 0 && (
                                <WarningText>Storyboard works best when shots contain images — add images to your shots to see thumbnails.</WarningText>
                            )}

                            {scenes && scenes.map((scene: any) => {

                                const shots = scene?.shots || [];
                                return (
                                    <section id={scene.id} key={scene.id} className="scene-block" ref={sectionRefs.current[scene.id]}>
                                        <Link to={`/films/${filmId}/scenes/${scene.id}/edit`}><h3 className="scene-heading">{scene.number}. {scene.settingName}</h3></Link>
                                        <div className="shots-grid" style={{ gridTemplateColumns: `repeat(${imagesPerRow}, 1fr)`, gap: shotsGap }}>
                                            {shots.map(shot => {
                                                const images = getShotImages(shot);
                                                const primary = images && images.length > 0 ? images[0] : null;
                                                const info = primary ? analyzed[primary] : { useBox: true };
                                                const showBox = !info || info.useBox;
                                                const textClass = info && info.textColor === "dark" ? "image-label-dark" : "image-label-light";

                                                // Render one card per image. If no images, render one placeholder card.
                                                if (!images || images.length === 0) {
                                                    return (
                                                        <Link to={`/films/${filmId}/scenes/${scene.id}/shots/${shot.id}/edit`} className="shot-card" key={shot.id}>
                                                            <div className="shot-image-wrap no-image-wrap">
                                                                <div className={`image-label box`}>{shot.number}</div>
                                                                <div className="no-image">No image</div>
                                                                <ShotMeta
                                                                    shot={shot}
                                                                    includeDescription={includeDescription}
                                                                    includeShotSize={includeShotSize}
                                                                    includeLensFocalLength={includeLensFocalLength}
                                                                    includeCharacters={includeCharacters}
                                                                    includeMovement={includeMovement}
                                                                    includeNotes={includeNotes}
                                                                />
                                                            </div>
                                                        </Link>
                                                    );
                                                }

                                                return images.map((img, idx) => {
                                                    const src = img + (img.indexOf("?") === -1 ? "?t=" + imageToken : "&t=" + imageToken);
                                                    const infoPer = analyzed[img] || {};
                                                    const showBoxPer = infoPer.useBox || false;
                                                    const textClassPer = infoPer.textColor === "dark" ? "image-label-dark" : "image-label-light";
                                                    const letter = images.length > 1 ? String.fromCharCode(97 + idx) : "";
                                                    const labelText = `${shot.number}${letter}`;
                                                    const key = `${shot.id}-${idx}`;

                                                    return (
                                                        <React.Fragment key={key}>
                                                            <Link to={`/films/${filmId}/scenes/${scene.id}/shots/${shot.id}/edit`} className="shot-card">
                                                                <div className="shot-image-wrap">
                                                                    <div className={`image-label ${showBoxPer ? "box" : ""} ${!showBoxPer ? textClassPer : ""}`}>{labelText}</div>
                                                                    <img src={src} alt={`shot-${shot.number}-${idx}`} onLoad={(e) => onImageLoad(e, img)} />
                                                                </div>
                                                                <ShotMeta
                                                                    shot={shot}
                                                                    includeDescription={includeDescription}
                                                                    includeShotSize={includeShotSize}
                                                                    includeLensFocalLength={includeLensFocalLength}
                                                                    includeCharacters={includeCharacters}
                                                                    includeMovement={includeMovement}
                                                                    includeNotes={includeNotes}
                                                                />
                                                                {idx < images.length - 1 && <div className="image-arrow">→</div>}
                                                            </Link>
                                                        </React.Fragment>
                                                    );
                                                });
                                            })}
                                        </div>
                                        <Link to={`/films/${filmId}/scenes/${scene.id}/shots/add`} className="button mt-1">Add Shot</Link>
                                    </section>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </>}
        </div>
    );
};

export default Storyboard;
